import { useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { clonePlan, Plan } from '@cfa/pathway-fe-shared/xp';

import { createCurrentLanguageValue, itemByLanguage } from '@/utils/language';

import InputDialog from '@/components/Dialogs/InputDialog';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants from '@/constants';

import { userPrefsAtom } from '@/atoms/userPrefs';
import { buildDuplicatePlanName } from '@/routes/TrainingPlans/buildDuplicatePlanName';
import useIsValidPlanName from '@/routes/TrainingPlans/hooks/useIsValidPlanName';

interface DuplicatePlanProps {
  plan: Plan;
  refetchPlans: () => void;
  setShowDuplicatePlan: (show: boolean) => void;
  showDuplicatePlan: boolean;
}

const DuplicatePlan: React.FC<DuplicatePlanProps> = ({
  plan,
  refetchPlans,
  setShowDuplicatePlan,
  showDuplicatePlan,
}) => {
  const { t } = useTranslation();
  const { language: userLanguage } = useAtomValue(userPrefsAtom);
  const [showInvalidNameError, setShowInvalidNameError] = useState(false);
  const isValidPlanName = useIsValidPlanName({
    onInvalidName: () => setShowInvalidNameError(true),
  });

  const duplicatePlanDefaultName = buildDuplicatePlanName(
    itemByLanguage(plan.name, ''),
    t('TrainingPlans.duplicatePlanInputCopy'),
  );

  const onDuplicatePlan = async (name: string) => {
    const trimmedName = name
      .trim()
      .slice(0, Constants.FORMS.MAX_PLAN_NAME_LENGTH);
    const planId = plan.id;
    const ownerId = plan.ownerId;

    if (!isValidPlanName(trimmedName, ownerId)) {
      return;
    }

    const { error } = await clonePlan({
      path: { planId: planId },
      body: {
        name: createCurrentLanguageValue(trimmedName, userLanguage),
        ownerId,
      },
    });

    if (error) {
      datadogRum.addError(
        error instanceof Error
          ? error
          : new Error('Clone plan request failed', { cause: error }),
      );
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorCopyingPlan')}
        </ToastMessageBlock>
      ));
      return;
    }

    setShowDuplicatePlan(false);
    refetchPlans();
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {`${trimmedName} ${t('TrainingPlans.toastMessage.duplicated')}`}
      </ToastMessageBlock>
    ));
  };

  return (
    <>
      <InputDialog
        headerText={t('Generic.giveName', { object: t('Generic.plan') })}
        allowBlankInput={showDuplicatePlan}
        isOpen={showDuplicatePlan}
        onClose={() => setShowDuplicatePlan(false)}
        primaryButtonHandler={onDuplicatePlan}
        primaryButtonText={t('Button.create')}
        primaryButtonVariant="filled"
        secondaryButtonHandler={() => setShowDuplicatePlan(false)}
        secondaryButtonText={t('Button.cancel')}
        textInputDefaultValue={duplicatePlanDefaultName}
        textInputMaxLength={Constants.FORMS.MAX_PLAN_NAME_LENGTH}
        textInputPlaceholder={t('TrainingPlans.planNamePlaceholder')}
      />
      <ConfirmationModal
        bodyText={t('InvalidPlanName.paragraphText')}
        headerText={t('InvalidPlanName.errorHeader')}
        isError={true}
        isOpen={showInvalidNameError}
        onClose={() => setShowInvalidNameError(false)}
        primaryButtonHandler={() => setShowInvalidNameError(false)}
        primaryButtonText={t('Button.close')}
      />
    </>
  );
};
export default DuplicatePlan;
